import type { HTMLAttributeAnchorTarget } from 'react';
import type { To as RouterLinkTo } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import { ArrowRight as ArrowRightIcon } from '@mui/icons-material';
import type { StackTypeMap } from '@mui/material';
import { Stack, SvgIcon, Typography } from '@mui/material';
import type { OverridableComponent } from '@mui/material/OverridableComponent';
import { styled } from '@mui/material/styles';
import type { PopupState } from 'material-ui-popup-state/hooks';
import { bindHover } from 'material-ui-popup-state/hooks';

// ----------------------------------------------------------------------

type StyledProps = {
  isActive?: boolean;
  isPopupOpen?: boolean;
};

export type NavbarButtonProps = Omit<StyledProps, 'isPopupOpen'> & {
  hasSubmenu: boolean;
  iconComponent: React.ComponentType;
  to: RouterLinkTo;
  title: string;
  popupState: PopupState;
  target?: HTMLAttributeAnchorTarget;
};

// ----------------------------------------------------------------------

const StyledRoot = styled(Stack, {
  shouldForwardProp: propName => propName !== 'isActive' && propName !== 'isPopupOpen',
})<StyledProps>(
  ({ theme }) => ({
    width: '100%',
    padding: theme.spacing(1, 0),
    borderRadius: theme.spacing(0.5),
    textDecoration: 'none',
    color: theme.palette.neutral['700'],
    transition: theme.transitions.create(['color', 'background-color']),

    ':hover': {
      backgroundColor: theme.palette.custom.grey,
    },

    '.SidebarButtonIconsContainer': {
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },

    '.SidebarButtonMainIconContainer': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: 32,
      height: 32,
      borderRadius: '50%',
      backgroundColor: theme.palette.neutral['200'],
      transition: theme.transitions.create(['color', 'background-color']),
    },

    '.SidebarButtonMainIcon': {
      width: 24,
      height: 24,
    },

    '.SidebarButtonCaretIcon': {
      position: 'absolute',
      top: '50%',
      right: 0,
      transform: 'translateY(-50%)',
      width: 24,
      height: 24,
      color: '#9AA7B5',
    },
  }),
  ({ isActive }) =>
    isActive && {
      '.SidebarButtonMainIconContainer': {
        backgroundColor: '#0C63E7',
      },

      '.SidebarButtonMainIcon': {
        color: '#ffffff',
      },
    },
  ({ isPopupOpen, theme }) =>
    isPopupOpen && {
      backgroundColor: theme.palette.custom.grey,
    },
) as OverridableComponent<StackTypeMap<StyledProps>>;

// ----------------------------------------------------------------------

export const NavbarButton = (props: NavbarButtonProps) => {
  const { isActive, hasSubmenu, iconComponent, to, title, popupState, ...restOfProps } = props;

  return (
    <StyledRoot
      {...restOfProps}
      isActive={isActive}
      isPopupOpen={popupState.isOpen}
      component={RouterLink}
      to={to}
      spacing={0.5}
      {...bindHover(popupState)}
    >
      <div className="SidebarButtonIconsContainer">
        <div className="SidebarButtonMainIconContainer">
          <SvgIcon component={iconComponent} inheritViewBox className="SidebarButtonMainIcon" />
        </div>

        {hasSubmenu && <ArrowRightIcon className="SidebarButtonCaretIcon" />}
      </div>
      <Typography component="span" fontSize={10} align="center">
        {title}
      </Typography>
    </StyledRoot>
  );
};
